import { FC, useCallback, useMemo, useState } from 'react'
import { Form, Navigate, redirect, useNavigate, json } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import './welcome.css'

// COMPONENTS
import * as Icons from '@icons'
import { Column } from '@components/templates/column'
import Button from '@components/atoms/button'
import Card from '@components/atoms/card'
import Divider from '@components/atoms/divider'
import GroupBanner from '@components/organisims/group-banner'
import GroupProgressBar from '@components/molecules/group-progress-bar'
import PledgeList from '@components/organisims/pledge-list'
import PledgeSlot from '@components/molecules/pledge-slot'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { parseUserLimits } from '@schemas'
import { createAuthenticatedLoader, refreshClerkUser } from '@utils/loaders'
import { useGroupLoaderData, usePledgeOutletContext } from '..'
import { IoTClient } from '../../../../_mqtt/config'
import { getGroupStatus } from '@utils/temp-helpers'
import { isNonEmptyObject } from '@utils/type-checkers'


interface Props { }

const formId = 'group-create-pledge-default'
const defaultSlotsObject: { openSlots: Pledge[], claimedSlots: Pledge[] } = { openSlots: [], claimedSlots: [] }
const GroupWelcomeScreen: FC<Props> = () => {
    const { user: clerkUser, isSignedIn } = useUser()
    const [{ lockedSlots }, setState] = usePledgeOutletContext()
    const [processingSlots, setProcessingSlots] = useState<Record<string, boolean>>({})
    const [visibleLoadingSlots, setVisibleLoadingSlots] = useState<Record<string, boolean>>({})
    const group = useGroupLoaderData()
    const navigate = useNavigate()


    const { organizer } = group

    const selectedPledge = useMemo(() => (group.pledges || []).find((pledge) => {
        const item = lockedSlots[`${pledge.id}`]
        return (!!item && (item.status === 'claimed' || item.status === 'settling') && item.user.id == clerkUser?.id)
    }), [group.pledges, lockedSlots])
    const { openSlots } = useMemo(() => [...(group.pledges || [])].reduce((prev, pledge) => {
        if (!pledge.transactionId && !pledge.user?.id) return { ...prev, openSlots: [...prev.openSlots, pledge] }
        else return { ...prev, claimedSlots: [...prev.claimedSlots, pledge] }
    }, defaultSlotsObject), [group.pledges])

    const hasPledge = !!group.pledges?.find(({ user }) => (user?.id === clerkUser?.id))

    const { groupStatus } = getGroupStatus(group)
    const isOrganizer = isSignedIn ? organizer?.id === clerkUser.id : false

    function handleAdd() {
        if (isNonEmptyObject(selectedPledge)) toggleSlot(selectedPledge)

        navigate('../contribute')
    }

    function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault()

        if (!!selectedPledge?.amount) setState((currentState) => ({
            ...currentState,
            pledge: selectedPledge,
        }))

        if (!!selectedPledge?.amount) navigate('../pledge/pm')
        else throw new Error(`Pledge Amounts must be more than $1`)
    }

    const toggleSlot = useCallback(async (pledge: Pledge) => {
        const { id, amount = 0 } = pledge
        if (!id || !clerkUser) {
            console.error(`toggleSlot: ${!clerkUser ? 'clerk user is undefined' : 'pledge id is undefined'}`)
            return
        }

        // Prevent duplicate actions while loading
        if (processingSlots[id]) return;

        setProcessingSlots((prev) => ({ ...prev, [id]: true }))
        const timerId = setTimeout(() => {
            setVisibleLoadingSlots((prev) => ({ ...prev, [id]: true }))
        }, 2000)

        try {
            const timestamp = new Date().getTime()
            const formUser: User = { id: clerkUser.id, givenName: clerkUser.firstName, familyName: clerkUser.lastName, imageUrl: clerkUser.imageUrl }
            const newState = { ...lockedSlots }
            const item: LockingItem = !!newState[id] ? newState[id] : { status: 'open', updatedAt: timestamp }

            Object.keys(newState).forEach((key) => {
                const item = newState[key]
                if ((item.status === 'claimed' || item.status === 'settling') && item.user.id === formUser.id) newState[key] = { status: 'open', updatedAt: timestamp }
            })

            let shouldProceed = true
            switch (item.status) {
                case 'open':
                    newState[id] = { status: 'claimed', updatedAt: timestamp, user: formUser, amount }
                    break
                case 'claimed':
                    if (item.user.id !== formUser.id) shouldProceed = false
                    else newState[id] = { status: 'open', updatedAt: timestamp }
                    break
                case 'settling':
                case 'purchased':
                default:
                    shouldProceed = false
                    break
            }

            if (!shouldProceed) return

            let attempts = 0
            const maxAttempts = 3
            let success = false

            while (attempts < maxAttempts && !success) {
                attempts++
                try {
                    await IoTClient.getInstance().ensureConnection()

                    await IoTClient.getInstance().publishMessage(`/group/${group.id}/locking/`, {
                        Event: 'MODIFY',
                        Type: 'locking',
                        Item: newState
                    } satisfies LockingEvent, { retain: true })

                    success = true
                } catch (error) {
                    console.error(`Attempt ${attempts} failed:`, error)
                    setVisibleLoadingSlots((prev) => ({ ...prev, [id]: true }))

                    if (attempts < maxAttempts) {
                        await new Promise(resolve => setTimeout(resolve, 1000))
                    }
                }
            }
        } catch (error) {
            console.error(error)
        } finally {
            clearTimeout(timerId)
            setProcessingSlots((prev) => ({ ...prev, [id]: false }))
            setVisibleLoadingSlots((prev) => ({ ...prev, [id]: false }))
        }
    }, [clerkUser, lockedSlots, processingSlots, group.id])

    if (isOrganizer || hasPledge || groupStatus !== 'active') return <Navigate to={`/group/${group.id}`} replace={true} />
    else if (group.type === 'pending') return <Navigate to={`/account`} replace={true} />
    else if (group.type === 'open') return <Navigate to={`/group/${group.id}/contribute`} replace={true} />
    else if (group.type === 'merchant' && group.organizer === null) return <Navigate to={`/group/${group.id}/contribute`} replace={true} />
    else return (<>
        <Column.Content className="group-pledge-screen">
            <div className='group-banner-section' style={{ marginBottom: '1rem' }}>
                <GroupBanner group={group} isGroupMember={true} />
                <h4 style={{ color: 'var(--splitcard-gray-500)', textAlign: 'center' }}>{(group.type === 'merchant' && group.organizer === null) ? group.merchant?.name || 'Someone' : (`${group.organizer?.givenName || ''} ${group.organizer?.familyName || ''}`.trim() || 'Someone')} is Inviting You To Contribute!</h4>
            </div>
            <GroupProgressBar goal={group.targetAmount || 0 / 100} pledge={((group.collectedAmount || 0) / 100) + ((selectedPledge?.amount || 0) / 100)} />
            <Form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
                <h3 style={{ marginBottom: '1rem', textAlign: 'center' }}>Tap Your Name To Contribute</h3>
                {openSlots.map((pledge, index) => (
                    <PledgeSlot key={`${pledge.id}-${index}`}
                        group={group}
                        selected={!!selectedPledge && pledge.id === selectedPledge.id}
                        onClick={() => toggleSlot(pledge)}
                        status={lockedSlots[`${pledge.id}`]}
                        loading={visibleLoadingSlots[pledge.id]}
                        pledge={pledge} />
                ))}
                {group.type === 'both' && <>
                    {openSlots.length > 0 && <Divider margin={true} />}
                    <Card className='add-name-cell' onClick={handleAdd}>
                        <div className='flex flex-row items-center gap-4'>
                            <div className='icon-bubble'>
                                <Icons.Plus tint="var(--splitcard-gray-300)" />
                            </div>
                            <div className='flex flex-col'>
                                <p className='text-body text-black'>Don't see your name?</p>
                                <p className='text-body-2'>Tap here!</p>
                            </div>
                        </div>
                    </Card>
                </>}
            </Form>
            <div className="flex flex-col gap-4">
                <h3 style={{ margin: '1rem 0', textAlign: 'center' }}>Who's Joined</h3>
                <PledgeList group={group} showUnclaimedParticipants={false} showParticipantLabel={false} showOutline={true} listType="claimed" />
            </div>
            <Column.Footer>
                <Button form={formId} type="submit" disabled={!selectedPledge?.amount}>Next</Button>
            </Column.Footer>
        </Column.Content>
    </>)
}

const loader = createAuthenticatedLoader(async ({ params }, Clerk) => {
    await refreshClerkUser(Clerk)
    const userLimits = parseUserLimits(Clerk.user?.publicMetadata?.limits)
    if (!userLimits.joinGroups && params.groupId) return redirect(`/group/${params.groupId}`)

    return json(null, 200)
})

const WelcomeRoute: RouteObject = {
    path: "welcome",
    element: <GroupWelcomeScreen />,
    shouldRevalidate: () => false,
    loader,
}

export default WelcomeRoute
